/**
 * Screen 15 — Sales: what the till took, night by night.
 *
 * Read-only. The figures are the `PosDay` rollups the sync writes — one per
 * service date, recomputed from that night's sales every time something
 * changes — so this screen adds nothing up that the API has not already
 * settled, beyond totalling the window it is showing. See
 * docs/integrations.md and architecture.md D12.
 *
 * Unmapped items are shown rather than hidden, with their units counted: a
 * line nobody has mapped takes nothing off the shelf, and a visible gap in
 * the stock figures is the one somebody goes and closes.
 *
 * Every figure is integer cents on the wire and dollars on the screen; the
 * conversion is `dollars` below and nowhere else.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDays,
  clockOf,
  isDateString,
  POS_BACKFILL_DAYS,
  DEFAULT_PERIOD,
  periodLabel,
  reportKindOf,
  reportOfWindow,
  windowOf,
  type Period,
  serviceDate,
  type PosDay,
  type PosDayItem,
  type PosSyncState,
  type ReportRecord,
  type ServiceClock,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { hasFeature } from "../features.js";
import { PeriodPicker } from "../Period.js";
import { money } from "../format.js";
import type { ScreenProps } from "../screens.js";
import { salesApi, type PosDays, type PosWindow, type Reports } from "./api.js";
import "./sales.css";

/** More bars than this and the chart stops being one: a range is clipped to it. */
const MAX_BARS = 62;

/** How far back each night is compared: the same night the week before. */
const COMPARE_DAYS = 7;

/**
 * What the screen shows: this trading week, last week, or a range of nights.
 * Weeks are the venue's — its own first day and its own cut-off — so "this
 * week" here is the same week as the dashboard's and the wastage screen's.
 */
/**
 * The nights to fetch: the shared period, clipped to what the chart can
 * draw. A month is more nights than there are bars, and a hand-picked range
 * can be any length at all, so the newest are the ones kept.
 */
function fetchWindow(period: Period, clock: ServiceClock, now: Date): PosWindow {
  const { from, to } = windowOf(period, clock, now);
  const earliest = addDays(to, -(MAX_BARS - 1));
  return { from: from < earliest ? earliest : from, to };
}

const percentChange = (now: number, before: number): string | null => {
  if (before <= 0) return null;
  const pct = Math.round(((now - before) / before) * 100);
  return `${pct > 0 ? "+" : ""}${pct}%`;
};

/** What the screen holds once the answer has been made safe to draw. */
interface View {
  readonly connected: boolean;
  readonly linked: boolean;
  /** Newest first. */
  readonly days: readonly PosDay[];
  readonly window: PosWindow | null;
  readonly sync: PosSyncState | null;
}

/**
 * The API is newer than this screen's testing of it, so nothing is assumed:
 * a missing list is an empty one, a flag that is not `true` is false.
 */
function viewOf(res: PosDays): View {
  const days = Array.isArray(res.days) ? res.days.filter((d) => isDateString(d?.serviceDate)) : [];
  const window =
    isDateString(res.window?.from) && isDateString(res.window?.to) ? res.window : null;
  return {
    connected: res.connected === true,
    linked: res.linked === true,
    days: [...days].sort((a, b) => b.serviceDate.localeCompare(a.serviceDate)),
    window,
    sync: res.sync ?? null,
  };
}

/** Cents to the dollars `money` formats. Anything unreadable is nought. */
const dollars = (cents: number | null | undefined) =>
  money((typeof cents === "number" && Number.isFinite(cents) ? cents : 0) / 100);

/** Tills sell some things by weight, so a quantity may be a fraction. */
const units = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

const nightLabel = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString("en-AU", {
    timeZone: "UTC",
    weekday: "short",
    day: "2-digit",
    month: "short",
  });

const dayOfMonth = (date: string) => date.slice(8, 10);

function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "The sales could not be loaded";
}

/**
 * Every service date in the window, oldest first — a bar for the nights the
 * till took nothing too, so the chart is a calendar and a closed Monday reads
 * as a gap rather than vanishing.
 */
function datesIn(window: PosWindow): string[] {
  const dates: string[] = [];
  for (let date = window.from; date <= window.to && dates.length < MAX_BARS; date = addDays(date, 1)) {
    dates.push(date);
  }
  return dates;
}

/** The window's totals. Plain sums: refunds are already signed into each night. */
function totalsOf(days: readonly PosDay[]) {
  const sum = (pick: (d: PosDay) => number) =>
    days.reduce((n, d) => n + (Number.isFinite(pick(d)) ? pick(d) : 0), 0);
  return {
    takingsCents: sum((d) => d.takingsCents),
    orders: sum((d) => d.orders),
    cashCents: sum((d) => d.cashCents),
    cardCents: sum((d) => d.cardCents),
    otherCents: sum((d) => d.otherCents),
    tipCents: sum((d) => d.tipCents),
    unmappedUnits: sum((d) => d.unmappedUnits),
  };
}

/**
 * The reports as the screen holds them: nothing assumed of the API, same as
 * `viewOf`. An absent kind is an empty list, and anything without text is
 * dropped rather than drawn as a blank paragraph.
 */
function reportsOf(res: Partial<Reports> | undefined): Reports {
  const of = (list: readonly ReportRecord[] | undefined) =>
    (Array.isArray(list) ? list : []).filter(
      (r) => typeof r?.text === "string" && r.text.trim() !== "" && isDateString(r.from),
    );
  return { daily: of(res?.daily), weekly: of(res?.weekly), monthly: of(res?.monthly) };
}

const NO_REPORTS: Reports = { daily: [], weekly: [], monthly: [] };

/** A report's paragraphs. The model writes prose; blank lines separate it. */
function ReportText(props: { readonly text: string }) {
  return (
    <>
      {props.text
        .split(/\n{2,}/)
        .map((para) => para.trim())
        .filter((para) => para !== "")
        .map((para, i) => (
          <p className="sl-report-p" key={i}>
            {para}
          </p>
        ))}
    </>
  );
}

/**
 * When the period on show will get its own read, said plainly.
 *
 * A week still being traded has no weekly report because the week has not
 * finished; saying "none yet" without saying why reads like something is
 * broken. A hand-picked range never gets one, and that is worth saying too.
 */
function whenItComes(period: Period, closed: boolean): string {
  if (period.kind === "range") {
    return "A range picked by hand gets no read of its own — each night below has one.";
  }
  const unit = period.kind === "this-week" || period.kind === "last-week" ? "week" : "month";
  return closed
    ? `No read written for this ${unit}. Reports are switched on per organisation, under Organisation › Settings.`
    : `This ${unit} is still being traded. Its read is written once it closes.`;
}

export function SalesScreen(_props: ScreenProps) {
  const app = useApp();
  const { token, venueId } = app;
  const canManageOrg = app.me.can["org:manage"];
  // Reports are the chat's model on the chat's budget: an organisation
  // without it has none, and offering an empty block would say otherwise.
  const reportsPossible = hasFeature(app.me, "chat");
  /** Sync times are instants; they are read on the venue's clock. */
  const clock = clockOf(app.venueState?.venue);
  const tz = clock.timeZone;

  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const shown = useMemo(() => fetchWindow(period, clock, new Date()), [period, clock.timeZone, clock.rolloverHour, clock.weekStartsOn]);
  const [view, setView] = useState<View | null>(null);
  const [reports, setReports] = useState<Reports>(NO_REPORTS);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const detail = useRef<HTMLElement>(null);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      // A week further back than the period, for each night's comparison.
      setView(viewOf(await salesApi.days(token, venueId, { from: addDays(shown.from, -COMPARE_DAYS), to: shown.to })));
    } catch (err) {
      setError(message(err));
    } finally {
      setLoading(false);
    }
  }, [token, venueId, shown.from, shown.to]);

  /**
   * The reports for the same window, fetched apart from the nights and
   * never fatal: the figures are the screen, and a report that could not be
   * read must not take them down with it.
   */
  useEffect(() => {
    if (!venueId || !reportsPossible) return;
    let live = true;
    setReports(NO_REPORTS);
    salesApi
      .reports(token, venueId, shown)
      .then((res) => {
        if (live) setReports(reportsOf(res?.reports));
      })
      .catch(() => {
        if (live) setReports(NO_REPORTS);
      });
    return () => {
      live = false;
    };
  }, [token, venueId, shown.from, shown.to]);

  useEffect(() => {
    void load();
  }, [load]);

  const syncNow = async () => {
    if (!venueId) return;
    setSyncing(true);
    setError(null);
    setNote(null);
    try {
      const res = await salesApi.sync(token, venueId);
      // A run that finished with an error says so in the sync line below,
      // off the refetch; only a clean run earns a note.
      if (!res.sync?.lastError) {
        const n = res.sync?.lastRunSales ?? 0;
        // A first sync of a busy venue is weeks of orders and the request
        // has seconds; `complete: false` is the server stopping on purpose.
        const more =
          "There is more to read — it carries on by itself every fifteen minutes, or press Sync now again.";
        setNote(
          res.complete === false
            ? n > 0
              ? `${n} new ${n === 1 ? "sale" : "sales"} read from Square. ${more}`
              : `Still catching up with Square. ${more}`
            : n > 0
              ? `${n} new ${n === 1 ? "sale" : "sales"} read from Square.`
              : "Square has nothing new since the last sync.",
        );
      }
      await load();
    } catch (err) {
      setError(message(err));
    } finally {
      setSyncing(false);
    }
  };

  // Everything fetched, the comparison week included; then the period's own nights.
  const fetched = useMemo(() => view?.days ?? [], [view]);
  const byDate = useMemo(() => new Map(fetched.map((d) => [d.serviceDate, d])), [fetched]);
  const days = useMemo(
    () => fetched.filter((d) => d.serviceDate >= shown.from && d.serviceDate <= shown.to),
    [fetched, shown],
  );
  const dates = useMemo(() => datesIn(shown), [shown]);
  const totals = useMemo(() => totalsOf(days), [days]);
  /** The same nights a week earlier, for the comparison. */
  const priorTotals = useMemo(
    () => totalsOf(dates.flatMap((d) => byDate.get(addDays(d, -COMPARE_DAYS)) ?? [])),
    [dates, byDate],
  );
  const peakCents = useMemo(
    () =>
      Math.max(
        0,
        ...dates.flatMap((d) => [byDate.get(d)?.takingsCents ?? 0, byDate.get(addDays(d, -COMPARE_DAYS))?.takingsCents ?? 0]),
      ),
    [dates, byDate],
  );
  const today = serviceDate(new Date(), { timeZone: clock });

  /** The read of the period on show, when the period has closed and has one. */
  const periodKind = reportKindOf(period);
  const periodReport = useMemo(
    () => (periodKind ? reportOfWindow(reports[periodKind], shown) : null),
    [periodKind, reports, shown.from, shown.to],
  );
  /** Each night's read, by service date, for the night on show. */
  const reportByNight = useMemo(
    () => new Map(reports.daily.map((r) => [r.from, r])),
    [reports],
  );

  if (!venueId) {
    return (
      <div className="pad">
        <div className="msg err">You are not assigned to a venue, so there are no sales to show.</div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="pad">
        {error ? (
          <div className="msg err">
            {error}
            <div style={{ marginTop: 10 }}>
              <button className="btn sm" disabled={loading} onClick={() => void load()}>
                Try again
              </button>
            </div>
          </div>
        ) : (
          <div className="label">Reading the till…</div>
        )}
      </div>
    );
  }

  const toIntegrations = () => app.go({ kind: "org", params: { tab: "integrations" } });

  if (!view.connected || !view.linked) {
    return (
      <div className="screen">
        <div className="pad">
          <div className="sl-empty">
            <div className="serif t">
              {!view.connected
                ? "Square is not connected yet."
                : "This venue is not linked to a Square location."}
            </div>
            <p className="sl-note">
              {!view.connected
                ? "Once the organisation's Square account is connected and this venue is linked to its location, each night's takings and what sold appear here."
                : "Square is connected for the organisation, but nobody has said which of its locations this venue is. Until then no sales are read for it."}
            </p>
            {canManageOrg ? (
              <div>
                <button className="btn primary" onClick={toIntegrations}>
                  Open Organisation › Integrations
                </button>
              </div>
            ) : (
              <p className="sl-note">
                An owner or admin sets this up under Organisation › Integrations.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // The night on show: the one picked, or the latest when it has left the window.
  const day = (selected ? byDate.get(selected) : undefined) ?? days[0] ?? null;

  const pick = (date: string) => {
    setSelected(date);
    // Stacked, the detail sits under the list; bring it to the person rather
    // than leave them wondering whether the tap did anything.
    if (window.matchMedia("(max-width: 900px)").matches) {
      requestAnimationFrame(() =>
        detail.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    }
  };

  const lastRun = view.sync?.lastRunAt
    ? new Date(view.sync.lastRunAt).toLocaleString("en-AU", {
        timeZone: tz,
        day: "2-digit",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="screen">
      <div
        className="row screen-head"
        style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14, flexWrap: "wrap" }}
      >
        <div>
          <div className="label">
            {periodLabel(period, shown)} ·{" "}
            {nightLabel(shown.from)} to {nightLabel(shown.to)}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
            {days.length === 0
              ? "Nothing read from the till for these nights."
              : `${dollars(totals.takingsCents)} over ${days.length} ${days.length === 1 ? "night" : "nights"}.`}
          </div>
          {priorTotals.takingsCents > 0 && (
            <div className="sl-compare">
              {/* Always the same nights seven days back. For a week that is
                  the week before and reads better said that way; for a month
                  or a range it is not, and saying so would be wrong. */}
              {period.kind === "this-week" || period.kind === "last-week"
                ? "The week before"
                : "The same nights a week earlier"}
              : {dollars(priorTotals.takingsCents)}
              {percentChange(totals.takingsCents, priorTotals.takingsCents) && (
                <span className={totals.takingsCents >= priorTotals.takingsCents ? "pos" : "neg"}>
                  {" "}· {percentChange(totals.takingsCents, priorTotals.takingsCents)}
                </span>
              )}
              {period.kind === "this-week" && shown.to > today ? " · so far this week" : ""}
            </div>
          )}
        </div>
        <span className="spacer" />
        <PeriodPicker
          period={period}
          shown={shown}
          disabled={loading}
          onChange={setPeriod}
        />
        <button className="btn" disabled={syncing || loading} onClick={() => void syncNow()}>
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>

      <div className="sl-sync">
        <span className="label">
          {lastRun
            ? `Last sync ${lastRun} · Square is read every fifteen minutes`
            : `Not synced yet — the first run reads ${POS_BACKFILL_DAYS} days back`}
        </span>
        {view.sync?.lastError && (
          <div className="msg err">The last sync failed: {view.sync.lastError}</div>
        )}
        {error && <div className="msg err">{error}</div>}
        {note && !error && <div className="msg ok">{note}</div>}
        {period.kind === "range" && shown.from > (period.from <= period.to ? period.from : period.to) && (
          <div className="msg warn">That range is longer than {MAX_BARS} nights, so the newest {MAX_BARS} are shown.</div>
        )}
      </div>

      <div className="split">
        <div className="pane">
          <div className="scroll" style={{ padding: "0 20px 20px" }}>
            {/* ------------------------------------------------ the window */}
            <div className="sl-totals">
              <div>
                <div className="label">Takings</div>
                <div className="v">{dollars(totals.takingsCents)}</div>
              </div>
              <div>
                <div className="label">Orders</div>
                <div className="v">{totals.orders}</div>
              </div>
              <div>
                <div className="label">Cash · card · other</div>
                <div className="v sm">
                  {dollars(totals.cashCents)} · {dollars(totals.cardCents)} ·{" "}
                  {dollars(totals.otherCents)}
                </div>
              </div>
              <div>
                <div className="label">Tips</div>
                <div className="v sm">{dollars(totals.tipCents)}</div>
              </div>
            </div>

            {/* ------------------------------------------- the period's read */}
            {reportsPossible && (
            <section className="sl-report" aria-label="The read of this period">
              <div className="label">
                {periodKind === "monthly" ? "The month" : periodKind === "weekly" ? "The week" : "These nights"}
                {periodReport ? " · written after it closed" : ""}
              </div>
              {periodReport ? (
                <ReportText text={periodReport.text} />
              ) : (
                <p className="sl-report-none">{whenItComes(period, shown.to < today)}</p>
              )}
            </section>
            )}

            {dates.length > 0 && (
              <>
              <div
                className={`sl-bars${dates.length > 16 ? " dense" : ""}`}
                role="group"
                aria-label="Takings per night, beside the same night the week before"
              >
                {dates.map((date) => {
                  const night = byDate.get(date);
                  const before = byDate.get(addDays(date, -COMPARE_DAYS));
                  const share = (d: PosDay | undefined) =>
                    d && peakCents > 0 ? Math.max(0, d.takingsCents) / peakCents : 0;
                  const words = `${nightLabel(date)}: ${night ? dollars(night.takingsCents) : date > today ? "not yet" : "no sales"} · the week before ${before ? dollars(before.takingsCents) : "no sales"}`;
                  return (
                    <button
                      key={date}
                      className={`sl-bar${date > today ? " future" : ""}`}
                      aria-pressed={day?.serviceDate === date}
                      aria-label={words}
                      title={words}
                      disabled={!night}
                      onClick={() => pick(date)}
                    >
                      <span className="track">
                        <span className="fill prior" style={{ height: `${share(before) * 100}%` }} />
                        <span className="fill" style={{ height: `${share(night) * 100}%` }} />
                      </span>
                      <span className="d">{dates.length <= 8 ? nightLabel(date).split(" ")[0] : dayOfMonth(date)}</span>
                    </button>
                  );
                })}
              </div>
              <div className="sl-legend label">
                <span className="key now" /> These nights <span className="key prior" /> The same night the week before
              </div>
              </>
            )}

            {/* ------------------------------------------------ the nights */}
            <div className="table sl-table">
              <div className="thead">
                <span className="sl-c-date">Night</span>
                <span className="sl-c-orders">Orders</span>
                <span className="sl-c-split">Cash · card · other</span>
                <span className="sl-c-prior">Week before</span>
                <span className="sl-c-take">Takings</span>
              </div>
              {days.map((d) => (
                <button
                  className="trow"
                  key={d.serviceDate}
                  aria-current={day?.serviceDate === d.serviceDate}
                  onClick={() => pick(d.serviceDate)}
                >
                  <span className="sl-c-date">{nightLabel(d.serviceDate)}</span>
                  <span className="sl-c-orders">
                    {d.orders} {d.orders === 1 ? "order" : "orders"}
                  </span>
                  <span className="sl-c-split">
                    {dollars(d.cashCents)} · {dollars(d.cardCents)}
                    {d.otherCents ? ` · ${dollars(d.otherCents)}` : ""}
                  </span>
                  <span className="sl-c-prior">
                    {(() => {
                      const before = byDate.get(addDays(d.serviceDate, -COMPARE_DAYS));
                      if (!before) return "—";
                      const change = percentChange(d.takingsCents, before.takingsCents);
                      return (
                        <>
                          {dollars(before.takingsCents)}
                          {change && <span className={d.takingsCents >= before.takingsCents ? "pos" : "neg"}> {change}</span>}
                        </>
                      );
                    })()}
                  </span>
                  <span className="sl-c-take">{dollars(d.takingsCents)}</span>
                </button>
              ))}
              {days.length === 0 && (
                <div className="pad label">
                  {loading
                    ? "Reading the till…"
                    : "No sales in this window. Sync now reads Square straight away."}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* -------------------------------------------------- one night */}
        <aside className="aside" ref={detail}>
          {day ? (
            <NightDetail
              day={day}
              report={reportByNight.get(day.serviceDate) ?? null}
              onMap={canManageOrg ? toIntegrations : null}
            />
          ) : (
            <div className="pad label">Pick a night to see what sold.</div>
          )}
        </aside>
      </div>
    </div>
  );
}

function NightDetail(props: {
  readonly day: PosDay;
  /** The night's own read, when the schedule has written one. */
  readonly report: ReportRecord | null;
  readonly onMap: (() => void) | null;
}) {
  const { day } = props;
  const items: readonly PosDayItem[] = Array.isArray(day.items) ? day.items : [];
  const unmapped = day.unmappedUnits > 0 ? day.unmappedUnits : 0;

  return (
    <>
      <div className="row" style={{ padding: "16px 20px 0" }}>
        <div className="label">Night · {nightLabel(day.serviceDate)}</div>
      </div>

      <div className="scroll" style={{ padding: "12px 20px 20px" }}>
        {props.report && (
          <section className="sl-report night" aria-label="The read of this night">
            <ReportText text={props.report.text} />
          </section>
        )}

        <div className="sl-headline">
          <span className="label">Takings, refunds off</span>
          <span className="v">{dollars(day.takingsCents)}</span>
          <span className="s">
            {day.orders} {day.orders === 1 ? "order" : "orders"}
            {day.refunds ? ` · ${day.refunds} ${day.refunds === 1 ? "refund" : "refunds"}` : ""}
          </span>
        </div>

        <div className="kv">
          <span className="k">Cash</span>
          <span className="v">{dollars(day.cashCents)}</span>
        </div>
        <div className="kv">
          <span className="k">Card</span>
          <span className="v">{dollars(day.cardCents)}</span>
        </div>
        <div className="kv">
          <span className="k">Other tender</span>
          <span className="v">{dollars(day.otherCents)}</span>
        </div>
        <div className="kv">
          <span className="k">Tips, included</span>
          <span className="v">{dollars(day.tipCents)}</span>
        </div>
        <div className="kv">
          <span className="k">GST, included</span>
          <span className="v">{dollars(day.taxCents)}</span>
        </div>
        <div className="kv">
          <span className="k">Discounts given</span>
          <span className="v">{dollars(day.discountCents)}</span>
        </div>
        <div className="kv">
          <span className="k">Refunded</span>
          <span className={`v ${day.refundCents > 0 ? "neg" : ""}`}>{dollars(day.refundCents)}</span>
        </div>

        <div className="label sl-items-head">
          Items sold · {items.length}
          {unmapped > 0 ? ` · ${units(unmapped)} unmapped ${unmapped === 1 ? "unit" : "units"}` : ""}
        </div>

        {unmapped > 0 && (
          <div className="sl-unmapped">
            <span>
              {units(unmapped)} {unmapped === 1 ? "unit" : "units"} sold on items nobody has
              mapped. They count towards takings and took nothing off stock.
            </span>
            {props.onMap && (
              <button className="btn sm" onClick={props.onMap}>
                Map till items
              </button>
            )}
          </div>
        )}

        <div className="sl-items">
          {items.map((item) => (
            <div className="sl-item" key={item.key}>
              <span className="nm">
                {item.name || "Unnamed item"}
                {item.variationName ? <span className="var"> · {item.variationName}</span> : null}
                {!item.mapped && <span className="flag warn">Unmapped</span>}
              </span>
              <span className="q">{units(item.quantity)}</span>
              <span className="g">{dollars(item.grossCents)}</span>
            </div>
          ))}
          {items.length === 0 && <div className="label">No item lines on this night.</div>}
        </div>
      </div>
    </>
  );
}
